/**
 * Enum Constants — single source of truth for all status/stage/environment values.
 *
 * These MUST match the database enum types and column constraints.
 * If a DB enum changes, update here and all references update automatically.
 */

// ─── Job Status ───
// Matches: job.status column values (Postgres enum public.job_status)
//
// Phase 23.7 (adversarial use case discovery 2026-05-01) added PAUSED:
// surfaced via Phase 23.4 walk — customer workflow "pause this project
// for 2 weeks while we review budget" had no platform model. Migration
// 313 adds the enum value to public.job_status.
export const JOB_STATUS = {
  DRAFT: 'Draft',
  OPEN: 'Open',
  CLOSING_SOON: 'Closing Soon',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

// PAUSED is intentionally NOT in ACTIVE_JOB_STATUSES — a paused project
// shouldn't count toward staffing utilization, billing $19/active counts,
// or "active project" KPIs. It's also NOT in PAST_JOB_STATUSES — work can
// resume. New PAUSED_JOB_STATUSES bucket is its own state class.
export const ACTIVE_JOB_STATUSES = [JOB_STATUS.OPEN, JOB_STATUS.ACTIVE, JOB_STATUS.CLOSING_SOON];
export const PAUSED_JOB_STATUSES = [JOB_STATUS.PAUSED];
export const PAST_JOB_STATUSES = [JOB_STATUS.COMPLETED, JOB_STATUS.CANCELLED];
export const ALL_JOB_STATUSES = Object.values(JOB_STATUS);

export const JOB_STATUS_COLORS = {
  [JOB_STATUS.DRAFT]: 'var(--text-muted)',
  [JOB_STATUS.OPEN]: '#F5A623',
  [JOB_STATUS.CLOSING_SOON]: '#F0997B',
  [JOB_STATUS.ACTIVE]: '#5DCAA5',
  // Phase 23.7: coral + same as Closing Soon — both are "attention"
  // states without being terminal. Visually distinct from Active (teal)
  // and Completed (blue).
  [JOB_STATUS.PAUSED]: '#F0997B',
  [JOB_STATUS.COMPLETED]: '#85B7EB',
  [JOB_STATUS.CANCELLED]: '#ff6b6b',
};

// ─── Assignment Status ───
// Matches: assignment.status column values
export const ASSIGNMENT_STATUS = {
  ONBOARDING: 'Onboarding',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  PAUSED: 'Paused',
  TERMINATED: 'Terminated',
};

export const WORKING_ASSIGNMENT_STATUSES = [ASSIGNMENT_STATUS.ACTIVE, ASSIGNMENT_STATUS.ONBOARDING];

// ─── Application Stage ───
// Matches: application.stage column values
export const APP_STAGE = {
  APPLIED: 'Applied',
  UNDER_REVIEW: 'Under Review',
  ASSESSMENT: 'Assessment',
  SKILLS_EVAL: 'Skills Evaluation',
  ACCEPTED: 'Accepted',
  NOT_SELECTED: 'Not Selected',
  WITHDRAWN: 'Withdrawn',
};

export const PENDING_APP_STAGES = [APP_STAGE.APPLIED, APP_STAGE.UNDER_REVIEW, APP_STAGE.ASSESSMENT, APP_STAGE.SKILLS_EVAL];
export const TERMINAL_APP_STAGES = [APP_STAGE.ACCEPTED, APP_STAGE.NOT_SELECTED, APP_STAGE.WITHDRAWN];

export const APP_STAGE_COLORS = {
  [APP_STAGE.APPLIED]: { bg: 'rgba(250,199,117,0.1)', color: '#F5A623' },
  [APP_STAGE.UNDER_REVIEW]: { bg: 'rgba(133,183,235,0.1)', color: '#85B7EB' },
  [APP_STAGE.ASSESSMENT]: { bg: 'rgba(175,169,236,0.1)', color: '#AFA9EC' },
  [APP_STAGE.SKILLS_EVAL]: { bg: 'rgba(175,169,236,0.1)', color: '#AFA9EC' },
  [APP_STAGE.ACCEPTED]: { bg: 'rgba(93,202,165,0.1)', color: '#5DCAA5' },
  [APP_STAGE.NOT_SELECTED]: { bg: 'rgba(255,100,100,0.1)', color: '#ff6b6b' },
  [APP_STAGE.WITHDRAWN]: { bg: 'var(--bg-stat)', color: 'var(--text-muted)' },
};

// ─── Environment ───
// Matches: job.environment, assignment.environment column values
export const ENVIRONMENT = {
  PRODUCTION: 'production',
  TEST: 'test',
};

// ─── Dataset Status ───
// Matches: dataset.status column values (migration 238)
// State machine lives in src/utils/datasetStateMachine.js (Commit 3)
// Phase 5.75 D2 — stored state, admin-driven, audit-logged, NOT derived from batches
export const DATASET_STATUS = {
  ACTIVE: 'Active',
  DELIVERED: 'Delivered',
  ACCEPTED: 'Accepted',
  ARCHIVED: 'Archived',
  PAUSED: 'Paused',
};

export const ACTIVE_DATASET_STATUSES = [DATASET_STATUS.ACTIVE, DATASET_STATUS.PAUSED];
export const TERMINAL_DATASET_STATUSES = [DATASET_STATUS.ACCEPTED, DATASET_STATUS.ARCHIVED];
export const ALL_DATASET_STATUSES = Object.values(DATASET_STATUS);

export const DATASET_STATUS_COLORS = {
  [DATASET_STATUS.ACTIVE]: { bg: 'rgba(93,202,165,0.1)', color: '#5DCAA5' },      // teal — working
  [DATASET_STATUS.DELIVERED]: { bg: 'rgba(245,166,35,0.1)', color: '#F5A623' },   // gold — awaiting client
  [DATASET_STATUS.ACCEPTED]: { bg: 'rgba(133,183,235,0.1)', color: '#85B7EB' },   // blue — signed off
  [DATASET_STATUS.ARCHIVED]: { bg: 'var(--bg-stat)', color: 'var(--text-muted)' }, // muted — out of active
  [DATASET_STATUS.PAUSED]: { bg: 'rgba(240,153,123,0.1)', color: '#F0997B' },     // coral — attention
};

// ─── Dataset Source Platform ───
// Matches: dataset.source_platform column values (migration 238)
export const DATASET_SOURCE_PLATFORM = {
  CSV_UPLOAD: 'csv_upload',
  LABEL_STUDIO_WEBHOOK: 'label_studio_webhook',
  CVAT_WEBHOOK: 'cvat_webhook',
  EXTERNAL_API: 'external_api',
  INTERNAL: 'internal',
  DERIVED: 'derived',
};

export const DATASET_SOURCE_PLATFORM_LABELS = {
  [DATASET_SOURCE_PLATFORM.CSV_UPLOAD]: 'CSV Upload',
  [DATASET_SOURCE_PLATFORM.LABEL_STUDIO_WEBHOOK]: 'Label Studio',
  [DATASET_SOURCE_PLATFORM.CVAT_WEBHOOK]: 'CVAT',
  [DATASET_SOURCE_PLATFORM.EXTERNAL_API]: 'External API',
  [DATASET_SOURCE_PLATFORM.INTERNAL]: 'Internal',
  [DATASET_SOURCE_PLATFORM.DERIVED]: 'Derived',
};

// ─── Dataset License Type ───
// Matches: dataset.license_type column values (migration 238)
export const DATASET_LICENSE_TYPE = {
  CLIENT_OWNED: 'client_owned',
  CC0: 'cc0',
  CC_BY: 'cc_by',
  PROPRIETARY: 'proprietary',
  CUSTOM: 'custom',
  UNKNOWN: 'unknown',
};

export const DATASET_LICENSE_LABELS = {
  [DATASET_LICENSE_TYPE.CLIENT_OWNED]: 'Client-Owned',
  [DATASET_LICENSE_TYPE.CC0]: 'CC0 (Public Domain)',
  [DATASET_LICENSE_TYPE.CC_BY]: 'CC BY',
  [DATASET_LICENSE_TYPE.PROPRIETARY]: 'Proprietary',
  [DATASET_LICENSE_TYPE.CUSTOM]: 'Custom (see license URL)',
  [DATASET_LICENSE_TYPE.UNKNOWN]: 'Unknown — needs review',
};

// ─── Dataset Type ───
// Matches: dataset.dataset_type column values (migration 238)
export const DATASET_TYPE = {
  CLIENT_DELIVERY: 'client_delivery',
  INTERNAL_TRAINING: 'internal_training',
  BOUGHT_EXTERNAL: 'bought_external',
  DERIVED: 'derived',
};

export const DATASET_TYPE_LABELS = {
  [DATASET_TYPE.CLIENT_DELIVERY]: 'Client Delivery',
  [DATASET_TYPE.INTERNAL_TRAINING]: 'Internal Training Data',
  [DATASET_TYPE.BOUGHT_EXTERNAL]: 'Bought / External Source',
  [DATASET_TYPE.DERIVED]: 'Derived from Another Dataset',
};

// ─── Dataset PII Status ───
// Matches: dataset.pii_status column values (migration 238)
export const DATASET_PII_STATUS = {
  CONTAINS_PII: 'contains_pii',
  PII_FREE: 'pii_free',
  ANONYMIZED: 'anonymized',
  UNKNOWN: 'unknown',
};

export const DATASET_PII_LABELS = {
  [DATASET_PII_STATUS.CONTAINS_PII]: 'Contains PII',
  [DATASET_PII_STATUS.PII_FREE]: 'PII-Free',
  [DATASET_PII_STATUS.ANONYMIZED]: 'Anonymized',
  [DATASET_PII_STATUS.UNKNOWN]: 'Unknown — needs review',
};

// ─── Dataset Consent Status ───
// Matches: dataset.consent_status column values (migration 238)
export const DATASET_CONSENT_STATUS = {
  CONSENTED: 'consented',
  NOT_REQUIRED: 'not_required',
  PENDING: 'pending',
  UNKNOWN: 'unknown',
};

export const DATASET_CONSENT_LABELS = {
  [DATASET_CONSENT_STATUS.CONSENTED]: 'Consented',
  [DATASET_CONSENT_STATUS.NOT_REQUIRED]: 'Not Required',
  [DATASET_CONSENT_STATUS.PENDING]: 'Pending',
  [DATASET_CONSENT_STATUS.UNKNOWN]: 'Unknown — needs review',
};

// ─── User Roles ───
// Matches: individual.role and auth user_metadata.role
export const USER_ROLE = {
  ANNOTATOR: 'annotator',
  SPL: 'spl',
  ADMIN: 'admin',
  CLIENT: 'client',
  GOD: 'god',
};

// ─── Onboarding Step Status ───
// Matches: onboarding_progress.status column values
export const STEP_STATUS = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  SKIPPED: 'Skipped',
};

// ─── UI Theme Colors ───
// Centralized color constants for status indicators, buttons, badges
// These reference the CSS custom properties where possible
export const COLORS = {
  TEAL: '#3B6FD6',       // Primary action, success, active status
  GOLD: '#F5A623',       // Warning, pending, onboarding
  BLUE: '#85B7EB',       // Secondary, info, onboarding steps
  PURPLE: '#AFA9EC',     // Tertiary, god mode, assessment
  CORAL: '#F0997B',      // Accent
  PINK: '#ED93B1',       // Accent
  RED: '#ef4444',        // Error, danger, critical alerts
  WARNING: '#f59e0b',    // Warning alerts (slightly different from GOLD)
  SUCCESS: '#10b981',    // Success/info alerts
  MUTED: '#999',         // Fallback/unknown
};

// ─── Alert Severity Styles ───
export const ALERT_SEVERITY = {
  critical: { border: COLORS.RED, bg: `rgba(239,68,68,0.04)`, label: 'Critical' },
  warning: { border: COLORS.WARNING, bg: `rgba(245,158,11,0.04)`, label: 'Warning' },
  info: { border: COLORS.SUCCESS, bg: `rgba(16,185,129,0.04)`, label: 'Info' },
};

// ─── Alert Type Labels ───
export const ALERT_TYPE_LABELS = {
  overtime: 'OVERTIME',
  schedule: 'SCHEDULE',
  quality: 'QUALITY',
  activity: 'TRACKING',
  budget: 'BUDGET',
  batch_stuck: 'STUCK',
  revision_spiral: 'REVISIONS',
  escalation: 'ESCALATION',
};

// ─── Business Rule Defaults ───
// These should eventually come from org_config / system_config table
export const DEFAULTS = {
  MAX_HOURS_PER_WEEK: 40,
  STAFFING_HEALTHY_THRESHOLD: 0.5, // 50% staffed = healthy
  ACTIVITY_LOG_MAX: 50,
  AUTO_REFRESH_INTERVAL: 30000, // 30 seconds — balances freshness vs DB load at scale
  FALLBACK_RATE: 0, // Default rate when job has no rate set — 0 means "not set"
  SPL_OVERLOADED_THRESHOLD: 15, // Annotators managed before SPL is "overloaded"
  SPL_AT_RISK_ALERT_THRESHOLD: 2, // Alert count before SPL is "at risk"
  // Financial health thresholds (for boss dashboard)
  MARGIN_HEALTHY: 30,       // >= 30% margin = green
  MARGIN_WARNING: 20,       // >= 20% = yellow, < 20% = red
  UTILIZATION_HEALTHY: 80,  // >= 80% utilized = green
  UTILIZATION_WARNING: 60,  // >= 60% = yellow, < 60% = red
  DEFAULT_BILLING_MARKUP: 1.8, // Fallback client billing markup when not set on job/client
  MONTHLY_HOURS_ESTIMATE: 160, // ~40hrs/week * 4 weeks — fallback for budget estimation
  // Alert thresholds (eventually org-configurable)
  BATCH_STUCK_CRITICAL: 2.5,   // Batch duration ratio for critical alert
  BATCH_STUCK_WARNING: 1.5,    // Batch duration ratio for warning alert
  UNREVIEWED_CRITICAL_DAYS: 7, // Days unreviewed before critical alert
  UNREVIEWED_WARNING_DAYS: 3,  // Days unreviewed before warning alert
  REJECTION_SPIKE_THRESHOLD: 0.3, // 30% rejection rate triggers alert
  QUALITY_ALERT_THRESHOLD: 90,    // % below which quality alerts fire
  IDLE_THRESHOLD_DAYS: 3,         // days without activity before idle alert fires
  IDLE_WARNING_HOURS: 48,         // hours before amber indicator
  IDLE_CRITICAL_HOURS: 72,        // hours before red indicator
  // SLA thresholds
  SLA_QUALITY_BUFFER: 5,          // % within target = "at risk" (e.g., target 95%, at_risk at 90-95%)
  SLA_DELIVERY_BUFFER_DAYS: 7,    // days before SLA deadline = "at risk"

  // ─── Phase 5 — Intelligence Layer (projections + confidence bands) ───
  // Invariant #4 (North Star): every projection has a confidence band.
  // See src/utils/statistics.js + src/utils/projections/*.
  PROJECTION_BAND_CONFIDENCE: 0.80,           // 80% band (nominal)
  PROJECTION_BAND_Z: 1.282,                    // z-score for 80% symmetric band
  PROJECTION_BAND_P_LOW: 0.10,                 // P10 for percentile-method bands
  PROJECTION_BAND_P_HIGH: 0.90,                // P90 for percentile-method bands
  PROJECTION_MIN_ANNOTATORS_FOR_HIGH_CONF: 3,  // below this → 'medium' or lower
  PROJECTION_MIN_WEEKS_FOR_HIGH_CONF: 2,       // below this → 'medium' or lower
  PROJECTION_MIN_ANNOTATORS_FOR_MEDIUM_CONF: 2,// below this → 'low' or insufficient
  PROJECTION_MIN_WEEKS_FOR_MEDIUM_CONF: 1,     // below this → 'low'
  PROJECTION_MIN_SAMPLES_FOR_PERCENTILE: 30,   // reserved for future bootstrap
  PROJECTION_QUALITY_FACTOR_MAX: 10,           // cap on 100/accuracy (prevents ÷0 blow-up)
};

// ─── Helpers ───

export function isActiveJobStatus(status) {
  return ACTIVE_JOB_STATUSES.includes(status);
}

export function isPastJobStatus(status) {
  return PAST_JOB_STATUSES.includes(status);
}

export function isPendingAppStage(stage) {
  return PENDING_APP_STAGES.includes(stage);
}

export function getJobStatusColor(status) {
  return JOB_STATUS_COLORS[status] || 'var(--text-muted)';
}

export function getAppStageColor(stage) {
  return APP_STAGE_COLORS[stage] || { bg: 'var(--bg-stat)', color: 'var(--text-muted)' };
}
